import React from 'react'
import { Heading, Text, LoadingSpinner, Input, Select, Button } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { usePermission } from '../../hooks/usePermissions'
import { useAuthContext } from '../../contexts/AuthContext'
import { modulesService } from '../../services/modules'
import { comercialAtividadesService } from '../../services/comercialAtividades'
import { comercialCadastroService } from '../../services/comercialCadastro'
import type { ComercialLookupItem } from '../../types/comercialCadastro'
import type { ComercialActivity, OverallFeedback } from '../../types/comercialAtividades'
import { FEEDBACK_LABELS } from '../../types/comercialAtividades'
import AtividadesRegistroTab from './AtividadesRegistroTab'
import AtividadesDashboardTab from './AtividadesDashboardTab'

const MODULE_NAME = 'comercial_atividades'

type ActiveTab = 'registro' | 'dashboard'
type DestinationFilter = 'all' | 'broker' | 'custom'
type FeedbackFilter = 'all' | OverallFeedback

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  const maybe = error as { message?: string; code?: string }
  if (maybe?.message) return maybe.message
  if (maybe?.code) return `Erro (${maybe.code})`
  return 'Erro inesperado ao carregar atividades.'
}

const ComercialAtividades: React.FC = () => {
  const { profile } = useAuthContext()
  const [moduleInfo, setModuleInfo] = React.useState<{
    display_name: string
    description?: string | null
  } | null>(null)
  const [activeTab, setActiveTab] = React.useState<ActiveTab>('registro')
  const [activities, setActivities] = React.useState<ComercialActivity[]>([])
  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const { hasPermission: canViewValues } = usePermission(MODULE_NAME, 'view_values')

  const [activityTypes, setActivityTypes] = React.useState<ComercialLookupItem[]>([])
  const [visitObjectives, setVisitObjectives] = React.useState<ComercialLookupItem[]>([])
  const [lookupsLoading, setLookupsLoading] = React.useState(true)

  const [filterDateFrom, setFilterDateFrom] = React.useState('')
  const [filterDateTo, setFilterDateTo] = React.useState('')
  const [filterDestination, setFilterDestination] = React.useState<DestinationFilter>('all')
  const [filterBroker, setFilterBroker] = React.useState('')
  const [filterTypeId, setFilterTypeId] = React.useState('')
  const [filterObjectiveId, setFilterObjectiveId] = React.useState('')
  const [filterFeedback, setFilterFeedback] = React.useState<FeedbackFilter>('all')
  const [filterResponsibleId, setFilterResponsibleId] = React.useState('')

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const data = await comercialAtividadesService.listActivities()
      setActivities(data)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  React.useEffect(() => {
    const loadModule = async () => {
      const result = await modulesService.getByName(MODULE_NAME)
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name ?? 'Atividades',
          description: result.data.description ?? null,
        })
      }
    }
    void loadModule()
  }, [])

  React.useEffect(() => {
    const loadLookups = async () => {
      setLookupsLoading(true)
      try {
        const [types, objectives] = await Promise.all([
          comercialCadastroService.listActivityTypes(),
          comercialCadastroService.listVisitObjectives(),
        ])
        setActivityTypes(types)
        setVisitObjectives(objectives)
      } catch {
        // Lookups de filtro: falha silenciosa; a aba de registro trata erros do formulário
      } finally {
        setLookupsLoading(false)
      }
    }
    void loadLookups()
  }, [])

  React.useEffect(() => {
    if (!canViewValues && activeTab === 'dashboard') {
      setActiveTab('registro')
    }
  }, [canViewValues, activeTab])

  const responsibleOptions = React.useMemo(() => {
    const map = new Map<string, string>()
    activities.forEach((a) => {
      if (!map.has(a.commercial_responsible_user_id)) {
        map.set(a.commercial_responsible_user_id, a.commercial_responsible_name)
      }
    })
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  }, [activities])

  const filteredActivities = React.useMemo(() => {
    let list = activities

    if (filterDateFrom) {
      list = list.filter((a) => a.activity_date.slice(0, 10) >= filterDateFrom)
    }
    if (filterDateTo) {
      list = list.filter((a) => a.activity_date.slice(0, 10) <= filterDateTo)
    }
    if (filterDestination === 'broker') {
      list = list.filter((a) => Boolean(a.broker_id))
    } else if (filterDestination === 'custom') {
      list = list.filter((a) => !a.broker_id)
    }
    if (filterBroker.trim()) {
      const q = filterBroker.trim().toLowerCase()
      list = list.filter((a) => {
        const name = (a.broker?.name ?? '').toLowerCase()
        const scgp = a.broker?.id_scgp != null ? String(a.broker.id_scgp) : ''
        return name.includes(q) || scgp.includes(q)
      })
    }
    if (filterTypeId) {
      list = list.filter((a) => a.activity_type_id === filterTypeId)
    }
    if (filterObjectiveId) {
      list = list.filter((a) => a.visit_objective_id === filterObjectiveId)
    }
    if (filterFeedback !== 'all') {
      list = list.filter((a) => a.overall_feedback === filterFeedback)
    }
    if (!canViewValues && profile?.id) {
      list = list.filter((a) => a.commercial_responsible_user_id === profile.id)
    } else if (filterResponsibleId) {
      list = list.filter((a) => a.commercial_responsible_user_id === filterResponsibleId)
    }

    return list
  }, [
    activities,
    filterDateFrom,
    filterDateTo,
    filterDestination,
    filterBroker,
    filterTypeId,
    filterObjectiveId,
    filterFeedback,
    filterResponsibleId,
    canViewValues,
    profile?.id,
  ])

  const limparFiltros = () => {
    setFilterDateFrom('')
    setFilterDateTo('')
    setFilterDestination('all')
    setFilterBroker('')
    setFilterTypeId('')
    setFilterObjectiveId('')
    setFilterFeedback('all')
    setFilterResponsibleId('')
  }

  const tabClass = (tab: ActiveTab) =>
    `py-3 px-1 border-b-2 font-medium text-sm ${
      activeTab === tab
        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  return (
    <div className="flex-1 overflow-x-hidden overflow-y-auto">
        <PermissionGuard moduleName={MODULE_NAME} permissionType="show">
          <div className="min-h-full flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                {moduleInfo?.display_name ?? 'Atividades'}
              </Heading>
              {moduleInfo?.description && (
                <Text size="sm" color="muted" className="mt-1">
                  {moduleInfo.description}
                </Text>
              )}
            </div>

            <div className="px-6 pt-4 pb-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
                <Text size="sm" color="muted">
                  {filteredActivities.length}
                  {filteredActivities.length !== activities.length
                    ? ` de ${activities.length}`
                    : ''}{' '}
                  {filteredActivities.length === 1 ? 'registro' : 'registros'}
                </Text>
                <Button variant="ghost" size="md" onClick={limparFiltros}>
                  Limpar filtros
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Data de
                  </label>
                  <Input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Data até
                  </label>
                  <Input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Destino
                  </label>
                  <Select
                    value={filterDestination}
                    onChange={(e) => setFilterDestination(e.target.value as DestinationFilter)}
                  >
                    <option value="all">Todos</option>
                    <option value="broker">Corretor</option>
                    <option value="custom">Outro</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Corretor
                  </label>
                  <Input
                    type="text"
                    placeholder="Nome ou SCGP..."
                    value={filterBroker}
                    onChange={(e) => setFilterBroker(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Tipo
                  </label>
                  <Select value={filterTypeId} onChange={(e) => setFilterTypeId(e.target.value)}>
                    <option value="">Todos</option>
                    {activityTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Objetivo
                  </label>
                  <Select
                    value={filterObjectiveId}
                    onChange={(e) => setFilterObjectiveId(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {visitObjectives.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Feedback
                  </label>
                  <Select
                    value={filterFeedback}
                    onChange={(e) => setFilterFeedback(e.target.value as FeedbackFilter)}
                  >
                    <option value="all">Todos</option>
                    <option value="positivo">{FEEDBACK_LABELS.positivo}</option>
                    <option value="neutro">{FEEDBACK_LABELS.neutro}</option>
                    <option value="negativo">{FEEDBACK_LABELS.negativo}</option>
                  </Select>
                </div>
                {canViewValues && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Responsável Comercial
                    </label>
                    <Select
                      value={filterResponsibleId}
                      onChange={(e) => setFilterResponsibleId(e.target.value)}
                    >
                      <option value="">Todos</option>
                      {responsibleOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                  <button type="button" onClick={() => setActiveTab('registro')} className={tabClass('registro')}>
                    Registro de Atividades
                  </button>
                  {canViewValues && (
                    <button type="button" onClick={() => setActiveTab('dashboard')} className={tabClass('dashboard')}>
                      Dashboard
                    </button>
                  )}
                </nav>
              </div>
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center py-16">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                {pageError && (
                  <div className="px-6 pt-4">
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                      <Text size="sm">{pageError}</Text>
                    </div>
                  </div>
                )}

                {activeTab === 'registro' && (
                  <AtividadesRegistroTab
                    activities={filteredActivities}
                    totalCount={activities.length}
                    onDataChange={loadData}
                    canViewValues={canViewValues}
                    activityTypes={activityTypes}
                    visitObjectives={visitObjectives}
                    lookupsLoading={lookupsLoading}
                  />
                )}

                {canViewValues && activeTab === 'dashboard' && (
                  <AtividadesDashboardTab activities={filteredActivities} />
                )}
              </>
            )}
          </div>
        </PermissionGuard>
    </div>
  )
}

export default ComercialAtividades
